import React from "react";
import { NutriValues } from "../../data/nutriValues";
import { cn } from "../../lib/utils";
import { MacroBar } from "../ui/MacroBar";

/**
 * Einheitliche Gericht-/Mahlzeit-Card für Planen, Verzehr und Kochdialog.
 *
 * Kopf: optionaler Leading-Slot (z. B. Checkbox), Titel (fett), darunter
 * Person + kcal, optional die Makro-Leiste. Rechts die Aktionen (Stift,
 * Löschen). Der Body enthält die flachen `IngredientRow`s.
 */
export function DishCard(props: {
  title: string;
  personName?: string;
  kcal?: number;
  nutri?: NutriValues;
  leading?: React.ReactNode;
  actions?: React.ReactNode;
  expanded?: boolean;
  onToggleExpand?: () => void;
  dataCy?: string;
  titleDataCy?: string;
  borderClassName?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const expandable = !!props.onToggleExpand;
  const showBody = !expandable || props.expanded;
  const showSubtitle =
    !!props.personName || (props.kcal != null && props.kcal > 0);

  return (
    <div
      data-cy={props.dataCy}
      className={cn(
        "w-full overflow-hidden rounded-card border bg-surface-card shadow-card",
        props.borderClassName ?? "border-line",
        props.className,
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        {props.leading}

        <button
          type="button"
          data-cy={props.titleDataCy}
          onClick={props.onToggleExpand}
          aria-expanded={expandable ? props.expanded : undefined}
          disabled={!expandable}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-col items-start">
            <span className="block w-full truncate text-sm font-semibold text-ink">
              {props.title}
            </span>
            {showSubtitle && (
              <span className="mt-0.5 flex items-center gap-2 text-xs text-ink-muted">
                {props.personName && (
                  <span className="font-medium text-teal">
                    {props.personName}
                  </span>
                )}
                {props.kcal != null && props.kcal > 0 && (
                  <span>{Math.round(props.kcal)} kcal</span>
                )}
              </span>
            )}
          </span>
        </button>

        {props.actions && (
          <div className="flex shrink-0 items-center gap-2">
            {props.actions}
          </div>
        )}
      </div>

      {props.nutri && (
        <div className="px-3 pb-2">
          <MacroBar
            fat={props.nutri.fat}
            carbs={props.nutri.carbs}
            protein={props.nutri.protein}
            kcal={props.nutri.kcal}
            showLabels
            hideKcal
          />
        </div>
      )}

      {showBody && (
        <div className="border-t border-line/60">{props.children}</div>
      )}
    </div>
  );
}
